import { useAuthQuery } from '../auth'
import { ApiAgentRequest, request } from '../../utils'
import { FileMetaData, ProgramIntake } from '@applyboard/school-applications-types-lib'
import { SetAllFieldsOptional } from '@applyboard/school-backend-lib'
import { ApplicationState } from '..'
import { isUndefined } from 'lodash'
import { StudentApplication } from '../../components/Application/types'
import { PolicyEvaluationResult } from '../../components/PolicyBanner/types'

type GetApplicationProps = {
  id?: string
}

export type RawApplicationResponse = {
  data: StudentApplication
}

export type DeferralOptions = {
  availableIntakes: Array<ProgramIntake>
  helperText: string
  reasonsForDeferral: string[]
  deferralState: ApplicationState
}

export type School = {
  additionalDocuments: Array<SchoolAdditionalDocument>
}

export type SchoolAdditionalDocument = {
  hash: string
  fileName: string
  url: string
  uploadedAt: string
}

export type RawApplicationAggregationResponse = {
  data: {
    id: string
    type: 'application_aggregation'
    attributes: {
      application: StudentApplication
      deferralOptions: DeferralOptions | null
      school: School
      intakePolicyEvaluation?: PolicyEvaluationResult
    }
  }
}

export function useGetApplication(props: GetApplicationProps) {
  const { isLoading, isFetching, data } = useAuthQuery(
    ['applications', props.id],
    async () => {
      return await request<RawApplicationAggregationResponse>(
        new ApiAgentRequest(`/applications/${props.id}`, {
          method: 'GET',
        }),
        {
          isExpectedResponse: (res): res is RawApplicationAggregationResponse => res as boolean,
        },
      )
    },
    {
      enabled: !!props.id,
      refetchOnWindowFocus: false,
      select: app => ({ ...app, data: hackActiveFile(app) }),
    },
  )

  const application = data?.data.attributes.application || null
  const deferralOptions = data?.data.attributes.deferralOptions || null
  const school = data?.data.attributes.school || null
  const intakePolicyEvaluation = data?.data.attributes.intakePolicyEvaluation || null

  return {
    isLoadingApplication: isLoading,
    isFetchingApplication: isFetching,
    application,
    deferralOptions,
    school,
    intakePolicyEvaluation,
  }
}

// Until activeRecord is set appropriately, hack it in.
function hackActiveFile(
  app: RawApplicationAggregationResponse,
): RawApplicationAggregationResponse['data'] {
  const sections: string[] = []
  const application = app.data.attributes.application
  if (application.attributes?.files) {
    application.attributes.files = Object.entries(application.attributes.files).reduce(
      (acc, [key, itm]) => {
        if (!itm || !itm.sectionReference) return acc
        if (!isUndefined(itm.activeRecord)) {
          acc[key] = itm
          return acc
        }

        let active = itm.sectionReference === 'other'
        if (!sections.includes(itm.sectionReference)) {
          active = true
          sections.push(itm.sectionReference)
        }

        acc[key] = {
          ...itm,
          activeRecord: active,
        }
        return acc
      },
      {} as Record<string, SetAllFieldsOptional<FileMetaData>>,
    )
  }

  return {
    ...app.data,
    attributes: {
      ...app.data.attributes,
      application,
    },
  }
}
